
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import { Lock, LockOpen, Globe, ChevronDown, Search } from "lucide-react";
import { Solution } from "@/types/solutions";

interface SolutionListViewProps {
  solutions: Solution[];
  addToCart: (solution: Solution) => void;
  cart: Solution[];
  getAgencyColor: (agency: string) => string;
  getCommercialTypeColor: (type: string) => string;
  selectedAgencies: string[];
  setSelectedAgencies: (agencies: string[]) => void;
  selectedCreators: string[];
  setSelectedCreators: (creators: string[]) => void;
  selectedCommercialTypes: string[];
  setSelectedCommercialTypes: (types: string[]) => void;
  selectedContentTypes: string[];
  setSelectedContentTypes: (types: string[]) => void;
  solutionSearchQuery: string;
  setSolutionSearchQuery: (query: string) => void;
  priceSort: string;
  setPriceSort: (sort: string) => void;
}

const SolutionListView: React.FC<SolutionListViewProps> = ({
  solutions,
  addToCart,
  cart,
  getAgencyColor,
  getCommercialTypeColor,
  selectedAgencies,
  setSelectedAgencies,
  selectedCreators,
  setSelectedCreators,
  selectedCommercialTypes,
  setSelectedCommercialTypes,
  selectedContentTypes,
  setSelectedContentTypes,
  solutionSearchQuery,
  setSolutionSearchQuery,
  priceSort,
  setPriceSort
}) => {
  const [agencySearch, setAgencySearch] = useState("");
  const [creatorSearch, setCreatorSearch] = useState("");
  const [typeSearch, setTypeSearch] = useState("");
  const [contentTypeSearch, setContentTypeSearch] = useState("");

  const getContentTypeIcon = (contentType: string) => {
    switch (contentType) {
      case "Private":
        return <Lock className="h-5 w-5 text-gray-600" />;
      case "Public":
        return <Globe className="h-5 w-5 text-gray-600" />;
      default:
        return null;
    }
  };

  // Get unique values for dropdowns
  const uniqueAgencies = Array.from(new Set(solutions.map(s => s.agency)));
  const uniqueCreators = Array.from(new Set(solutions.map(s => s.curator)));
  const uniqueCommercialTypes = Array.from(new Set(solutions.map(s => s.commercialType)));
  const uniqueContentTypes = Array.from(new Set(solutions.map(s => s.contentType)));

  const filteredAgencies = uniqueAgencies.filter(agency => 
    agency.toLowerCase().includes(agencySearch.toLowerCase())
  );
  const filteredCreators = uniqueCreators.filter(creator => 
    creator.toLowerCase().includes(creatorSearch.toLowerCase())
  );
  const filteredCommercialTypes = uniqueCommercialTypes.filter(type => 
    type.toLowerCase().includes(typeSearch.toLowerCase())
  );
  const filteredContentTypes = uniqueContentTypes.filter(type => 
    type.toLowerCase().includes(contentTypeSearch.toLowerCase())
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-2 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 hover:scale-105">
                  <div className="flex items-center gap-1 text-gray-900">
                    Solution
                    <ChevronDown className="h-3 w-3" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border shadow-lg z-50">
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search solutions..."
                      value={solutionSearchQuery}
                      onChange={(e) => setSolutionSearchQuery(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableHead>
          <TableHead>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-2 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 hover:scale-105">
                  <div className="flex items-center gap-1 text-gray-900">
                    Channel
                    <ChevronDown className="h-3 w-3" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border shadow-lg z-50">
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search channels..."
                      value={agencySearch}
                      onChange={(e) => setAgencySearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
                {filteredAgencies.map((agency) => (
                  <DropdownMenuCheckboxItem
                    key={agency}
                    checked={selectedAgencies.includes(agency)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedAgencies([...selectedAgencies, agency]);
                      } else {
                        setSelectedAgencies(selectedAgencies.filter(a => a !== agency));
                      }
                    }}
                  >
                    {agency}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </TableHead>
          <TableHead>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-2 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 hover:scale-105">
                  <div className="flex items-center gap-1 text-gray-900">
                    Creator
                    <ChevronDown className="h-3 w-3" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="w-56 bg-white border shadow-lg z-50" 
                onCloseAutoFocus={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onFocusOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
              >
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search creators..."
                      value={creatorSearch}
                      onChange={(e) => setCreatorSearch(e.target.value)}
                      className="h-8"
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          // Apply filter and close dropdown
                          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
                        }
                      }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mb-2">Select multiple creators, then press Enter to apply</div>
                </div>
                <DropdownMenuSeparator />
                {filteredCreators.map((creator) => (
                  <DropdownMenuCheckboxItem
                    key={creator}
                    checked={selectedCreators.includes(creator)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedCreators([...selectedCreators, creator]);
                      } else {
                        setSelectedCreators(selectedCreators.filter(c => c !== creator));
                      }
                    }}
                    className="flex items-center gap-2"
                    onSelect={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {creator}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </TableHead>
          <TableHead>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-2 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 hover:scale-105">
                  <div className="flex items-center gap-1 text-gray-900">
                    Type
                    <ChevronDown className="h-3 w-3" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border shadow-lg z-50">
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search types..."
                      value={typeSearch}
                      onChange={(e) => setTypeSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
                <div className="mb-2 text-xs font-medium text-gray-500 px-2">Commercial Type</div>
                {filteredCommercialTypes.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={`commercial-${type}`}
                    checked={selectedCommercialTypes.includes(type)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedCommercialTypes([...selectedCommercialTypes, type]);
                      } else {
                        setSelectedCommercialTypes(selectedCommercialTypes.filter(t => t !== type));
                      }
                    }}
                  >
                    {type}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <div className="mb-2 text-xs font-medium text-gray-500 px-2">Content Type</div>
                {filteredContentTypes.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={`content-${type}`}
                    checked={selectedContentTypes.includes(type)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedContentTypes([...selectedContentTypes, type]);
                      } else {
                        setSelectedContentTypes(selectedContentTypes.filter(t => t !== type));
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {getContentTypeIcon(type)}
                      {type}
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </TableHead>
          <TableHead>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-2 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 hover:scale-105">
                  <div className="flex items-center gap-1 text-gray-900">
                    Price
                    <ChevronDown className="h-3 w-3" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border shadow-lg z-50">
                <DropdownMenuItem
                  onClick={() => setPriceSort("high-to-low")}
                  className={priceSort === "high-to-low" ? "bg-gray-100" : ""}
                >
                  Sort High to Low
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setPriceSort("low-to-high")}
                  className={priceSort === "low-to-high" ? "bg-gray-100" : ""}
                >
                  Sort Low to High
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setPriceSort("")}
                  className={priceSort === "" ? "bg-gray-100" : ""}
                >
                  Clear Sort
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {solutions.map((solution) => (
          <TableRow key={solution.id}>
            <TableCell className="font-medium">
              <div>
                <div className="flex items-center gap-2">
                  {getContentTypeIcon(solution.contentType)}
                  {solution.title}
                </div>
                <p className="text-xs text-gray-500 mt-1">{solution.category}</p>
                {solution.capturedDate && (
                  <p className="text-xs text-gray-400">Captured on {solution.capturedDate}</p>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={getAgencyColor(solution.agency)}>
                {solution.agency}
              </Badge>
            </TableCell>
            <TableCell>
              {(() => {
                const curatorLinks: Record<string, { route: string, displayName?: string }> = {
                  "Julia De Rosi": { route: "/julia-derosi-profile" },
                  "Heather Edwards": { route: "/heather-edwards-profile" },
                  "Charles Hardy": { route: "/charles-hardy-profile" },
                  "Shaan Patel": { route: "/shaan-profile", displayName: "Shaan Ray" },
                  "Shaan Ray": { route: "/shaan-profile" },
                  "Trey Fleming": { route: "/trey-profile" },
                  "Karen Watson": { route: "/karen-profile" },
                  "Morgan Eldred, PhD": { route: "/morgan-profile" },
                  "Mike Mosher": { route: "/mike-mosher-profile" },
                  "Jim Lawnin": { route: "/jim-profile" },
                  "Jess Simm": { route: "/profile-jess" },
                  "Pete Dumont": { route: "/pete-profile" },
                  "Chris Clayton": { route: "/chris-profile" },
                  "Corey Vickers": { route: "/corey-profile" },
                  "Miguel Escobedo": { route: "/miguel-profile" },
                  "Yannis Kalfoglou, PhD": { route: "/yannis-profile" },
                  "Steven Jast": { route: "/steven-profile" },
                  "Eglantine Pauvarel Moss": { route: "/profile-eglantine" },
                  "Eglantine Kuntz": { route: "/eglantine-profile" },
                  "Ozlem Tuncel Toplu": { route: "/profile-ozlem" },
                  "Ozlem Ergun": { route: "/ozlem-profile" }
                };

                const curatorInfo = curatorLinks[solution.curator];
                if (curatorInfo) {
                  return (
                    <Link 
                      to={curatorInfo.route} 
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {curatorInfo.displayName || solution.curator}
                    </Link>
                  );
                }
                return solution.curator;
              })()}
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                {solution.commercialType}
              </Badge>
            </TableCell>
            <TableCell>${solution.price.toLocaleString()}</TableCell>
            <TableCell>
              <Button 
                size="sm" 
                onClick={() => addToCart(solution)}
                disabled={cart.some(item => item.id === solution.id)}
              >
                {cart.some(item => item.id === solution.id) ? 'Added' : 'Add to Cart'}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SolutionListView;
